<script setup>
import { ref, computed, watch, shallowRef, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'
import { getWidgetTypes, getWidgetType, getWidgetConfigComponent } from './widgetRegistry'

const props = defineProps({
  widget: {
    type: Object,
    default: null
  }
})

const mainStore = useMainStore()

// Determine mode based on widget prop
const isEditMode = computed(() => !!props.widget?.id)

// Step management (only for create mode)
const step = ref(1)
const selectedWidgetType = ref(null)

// Dynamic config component
const ConfigComponent = shallowRef(null)

// Widget types grouped by category
const widgetTypes = computed(() => getWidgetTypes())

const widgetsByCategory = computed(() => {
  const groups = {}
  widgetTypes.value.forEach(widget => {
    const category = widget.category || 'Other'
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(widget)
  })
  return groups
})

// Get selected widget type info
const selectedWidgetTypeInfo = computed(() => {
  if (isEditMode.value && props.widget) {
    return getWidgetType(props.widget.widget_type)
  }
  return selectedWidgetType.value ? getWidgetType(selectedWidgetType.value) : null
})

// Header title
const headerTitle = computed(() => {
  if (isEditMode.value) {
    return 'Configure Widget'
  }
  return step.value === 1 ? 'Select Widget Type' : 'Configure Widget'
})

// Load config component when widget type is selected or in edit mode
async function loadConfigComponent(typeId) {
  const loader = getWidgetConfigComponent(typeId)
  if (loader) {
    try {
      const module = await loader()
      ConfigComponent.value = module.default
    } catch (e) {
      console.error('Failed to load config component:', e)
      ConfigComponent.value = null
    }
  }
}

// Select widget type in create mode
function selectWidgetType(typeId) {
  selectedWidgetType.value = typeId
  loadConfigComponent(typeId)
  step.value = 2
}

// Go back to widget type selection
function goBack() {
  step.value = 1
  selectedWidgetType.value = null
  ConfigComponent.value = null
}

// Handle config submit
function onConfigSubmit() {
  mainStore.close_slideover()
}

// Handle config cancel
function onConfigCancel() {
  if (isEditMode.value) {
    mainStore.close_slideover()
  } else {
    goBack()
  }
}

// Initialize for edit mode
onMounted(() => {
  if (isEditMode.value && props.widget) {
    loadConfigComponent(props.widget.widget_type)
  }
})

// Watch for widget changes in edit mode
watch(() => props.widget, (newWidget) => {
  if (newWidget?.id) {
    loadConfigComponent(newWidget.widget_type)
  }
}, { immediate: true })
</script>

<template>
  <div class="widget-slideover">
    <!-- Header -->
    <div class="slideover-header">
      <button v-if="!isEditMode && step === 2" class="btn-back" @click="goBack">
        <font-awesome-icon icon="arrow-left" />
      </button>
      <h2 class="slideover-title">{{ headerTitle }}</h2>
    </div>

    <!-- Create Mode: Step 1 - Widget Type Selection -->
    <div v-if="!isEditMode && step === 1" class="step-content">
      <div
        v-for="(widgets, category) in widgetsByCategory"
        :key="category"
        class="widget-category"
      >
        <h3 class="category-heading">{{ category }}</h3>
        <div class="widget-types-grid">
          <div
            v-for="wt in widgets"
            :key="wt.id"
            class="widget-type-card"
            @click="selectWidgetType(wt.id)"
          >
            <div class="card-icon">
              <font-awesome-icon :icon="wt.icon" />
            </div>
            <div class="card-content">
              <h3 class="card-title">{{ wt.name }}</h3>
              <p class="card-description">{{ wt.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Mode: Step 2 - Configuration OR Edit Mode -->
    <div v-else class="step-content config-step">
      <!-- Selected Widget Type Info -->
      <div v-if="selectedWidgetTypeInfo" class="selected-widget-info">
        <div class="info-icon">
          <font-awesome-icon :icon="selectedWidgetTypeInfo.icon" />
        </div>
        <div class="info-text">
          <span class="info-name">{{ selectedWidgetTypeInfo.name }}</span>
          <span class="info-description">{{ selectedWidgetTypeInfo.description }}</span>
        </div>
      </div>

      <!-- Dynamic Config Component -->
      <component
        v-if="ConfigComponent"
        :is="ConfigComponent"
        :widget-id="isEditMode ? widget.id : null"
        @submit="onConfigSubmit"
        @cancel="onConfigCancel"
      />

      <!-- Loading State -->
      <div v-else class="loading-state">
        <font-awesome-icon icon="spinner" spin />
        Loading configuration...
      </div>
    </div>
  </div>
</template>

<style scoped>
.widget-slideover {
  padding: 1.5rem;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* Header */
.slideover-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.btn-back {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #f3f4f6;
}

.slideover-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0;
  font-family: 'Jura', sans-serif;
}

/* Step Content */
.step-content {
  flex: 1;
  overflow-y: auto;
}

.config-step {
  display: flex;
  flex-direction: column;
}

/* Widget Category */
.widget-category {
  margin-bottom: 2rem;
}

.widget-category:last-child {
  margin-bottom: 0;
}

.category-heading {
  font-size: 0.875rem;
  font-weight: 600;
  color: #f3f4f6;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 -1.5rem 0.75rem -1.5rem;
  padding: 0 1.5rem 0.5rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Widget Types Grid */
.widget-types-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.widget-type-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;
}

.widget-type-card:hover {
  background: rgba(115, 87, 210, 0.1);
  border-color: rgba(115, 87, 210, 0.3);
}

.card-icon {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 87, 210, 0.15);
  border-radius: 0.5rem;
  color: #7357D2;
  font-size: 1rem;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0 0 0.25rem 0;
}

.card-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
}

/* Selected Widget Info */
.selected-widget-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(115, 87, 210, 0.1);
  border: 1px solid rgba(115, 87, 210, 0.2);
  border-radius: 0.5rem;
  margin-bottom: 1.25rem;
}

.info-icon {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 87, 210, 0.2);
  border-radius: 0.5rem;
  color: #7357D2;
  font-size: 1rem;
}

.info-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.info-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #f3f4f6;
}

.info-description {
  font-size: 0.8125rem;
  color: #9ca3af;
}

/* Loading State */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem;
  color: #9ca3af;
  font-size: 0.9375rem;
}
</style>
